import { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
// Remover import de generateSessionId
// import { generateSessionId } from '../utils/session';
import Header from './Header';
import ChatInput from './ChatInput';
import ChatHistory from './ChatHistory';
// Feedback foi movido para Sidebar
// import Feedback from './Feedback';
import { supabase } from '../supabaseClient';

// --- Styled Components ---

const ChatViewContainer = styled.main`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Garantir que este container não transborde */
`;

const ChatHistoryContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 1rem;
  width: 100%;
  background-color: #0b141a; /* Fundo do histórico dark */
  position: relative; /* Para posicionar o indicador */
  min-height: 0; /* Correção para cálculo de altura em flex container */
`;

const FooterContainer = styled.div`
  width: 100%;
  background: #202c33; /* Fundo da área inferior dark */
  /* Remover padding-bottom para o input ficar colado */
  border-top: 1px solid #263038; /* Borda sutil */
  flex-shrink: 0; /* Impedir que o rodapé encolha */
`;

const Loader = styled.div`
  margin: 1.5rem auto;
  width: 36px;
  height: 36px;
  border: 4px solid ${({ theme }) => theme.colors.primary || '#00a884'};
  border-top: 4px solid transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// Indicador "Digitando..."
const typing = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0.8rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: #8696a0;
  align-self: flex-start;
  background: #202c33; /* Fundo igual ao balão da IA */
  border-radius: 0.5rem;
  border-top-left-radius: 0; /* Rabicho */
  max-width: fit-content; /* Ajustar largura ao conteúdo */
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);

  span {
    display: inline-block;
    width: 6px;
    height: 6px;
    background-color: #8696a0;
    border-radius: 50%;
    margin-left: 4px;
    animation: ${typing} 1s infinite;
  }
  span:nth-child(1) { animation-delay: 0s; }
  span:nth-child(2) { animation-delay: 0.2s; }
  span:nth-child(3) { animation-delay: 0.4s; }
`;

// --- Componente ---

// Receber sessionId como prop
export default function ChatView({ sessionId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false); // Estado para indicador

  const historyContainerRef = useRef(null); // Manter ref para scroll

  // Efeito para rolar para o final
  useEffect(() => {
    if (historyContainerRef.current) {
      historyContainerRef.current.scrollTop = historyContainerRef.current.scrollHeight;
    }
  }, [history, isTyping]); // Rolar quando histórico ou typing muda

  // Função auxiliar para adicionar mensagens com delay (modificada)
  const addMessageWithDelay = (messages, index = 0) => {
    if (index >= messages.length) {
      // Se não houver mais mensagens, desativar typing imediatamente
      setIsTyping(false);
      return;
    }

    const part = messages[index].trim();
    if (part) { // Adicionar apenas se não for vazio após trim
        setHistory((h) => [...h, { from: 'ia', text: part, sessionId: sessionId }]);
    }

    // Se houver próxima mensagem, agendar com delay
    if (index + 1 < messages.length) {
      setTimeout(() => {
        addMessageWithDelay(messages, index + 1);
      }, 500); // Delay de 500ms entre as partes
    } else {
        // Se for a última, desativar typing imediatamente após adicionar
        setIsTyping(false);
    }
  };


  const sendMessage = async (msg) => {
    setHistory((h) => [...h, { from: 'user', text: msg, sessionId: sessionId }]);
    setLoading(true);
    setIsTyping(false); // Resetar typing ao enviar nova msg

    try {
      // 1. Salvar mensagem do usuário
      try {
        const { error: userMsgError } = await supabase
          .from('playground_chat_messages')
          .insert([{ session_id: sessionId, sender: 'user', message_text: msg }]);
        if (userMsgError) throw userMsgError;
      } catch (dbError) {
        console.error("Erro ao salvar msg usuário:", dbError);
      }

      // 2. Enviar para o n8n
      await fetch('https://webhooks.multimachine.com.br/webhook/2818e415-2695-46cd-b09f-927c0063d16f', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, sessionId: sessionId }),
      });

      // 3. Polling no Supabase pela resposta
      let tentativas = 0;
      const maxTentativas = 30; // Aumentado para 30 tentativas (~45 segundos de espera total)
      const intervalo = 1500;
      let respostaCompleta = null;

      while (tentativas < maxTentativas) {
        try {
          const { data: pendingData, error: selectError } = await supabase
            .from('playground_pending_replies')
            .select('reply')
            .eq('session_id', sessionId)
            .maybeSingle();

          if (selectError) {
            console.error(`Polling Supabase (select) error:`, selectError);
          } else if (pendingData) {
            respostaCompleta = pendingData.reply;
            const { error: deleteError } = await supabase
              .from('playground_pending_replies')
              .delete()
              .eq('session_id', sessionId);
            if (deleteError) console.error(`Polling Supabase (delete) error:`, deleteError);
            else console.log(`Resposta para ${sessionId} obtida e removida.`);
            break;
          }
        } catch (pollError) {
           console.error("Erro inesperado no polling Supabase:", pollError);
        }
        await new Promise(r => setTimeout(r, intervalo));
        tentativas++;
      }

      // 4. Processar e exibir resposta da IA
      if (respostaCompleta) {
        // Salvar a resposta *completa* no histórico de chat
         try {
          const { error: iaMsgError } = await supabase
            .from('playground_chat_messages')
            .insert([{ session_id: sessionId, sender: 'ia', message_text: respostaCompleta }]);
          if (iaMsgError) throw iaMsgError;
        } catch (dbError) {
          console.error("Erro CRÍTICO ao salvar msg IA:", dbError);
          // Adicionar mensagem de erro ao histórico local para indicar falha no salvamento
          setHistory((h) => [...h, { from: 'ia', text: `[Erro ao salvar no DB: ${dbError.message}]`, sessionId: sessionId }]);
          // Considerar não exibir a mensagem original da IA se o salvamento falhou
          // return; // Poderia parar aqui para não exibir a msg não salva
        }

        // Dividir a resposta por quebras de linha duplas
        const messageParts = respostaCompleta.split('\n\n').filter(part => part.trim() !== '');

        if (messageParts.length > 0) {
          // Adicionar a primeira parte imediatamente
          setHistory((h) => [...h, { from: 'ia', text: messageParts[0], sessionId: sessionId }]);

          // Se houver mais partes, ativar "digitando" e adicionar com delay
          if (messageParts.length > 1) {
             setIsTyping(true);
             // Adicionar as partes restantes com delay, começando da segunda parte (índice 1)
             // Usar um pequeno delay inicial antes de começar a "digitar" as partes restantes
             setTimeout(() => {
                addMessageWithDelay(messageParts, 1); // Começa do índice 1
             }, 500); // Delay inicial de 500ms
          } else {
             // Se houver apenas uma parte, garantir que isTyping esteja false
             setIsTyping(false);
          }
        } else {
           // Se messageParts estiver vazio, garantir que isTyping esteja false
           setIsTyping(false);
        }

      } else {
        // Timeout
        setHistory((h) => [...h, { from: 'ia', text: 'O agente não respondeu a tempo. Tente novamente.', sessionId: sessionId }]);
      }
    } catch (error) {
      console.error("Erro no fluxo de mensagem:", error);
      setHistory((h) => [...h, { from: 'ia', text: 'Ocorreu um erro. Verifique o console ou tente novamente.', sessionId: sessionId }]);
    } finally {
       setLoading(false); // Garantir que loading termine
       // Não desativar isTyping aqui, pois addMessageWithDelay ou a lógica acima controla isso
    }
  };

  return (
    <ChatViewContainer>
      <Header />
      {/* Passar a ref para o container */}
      <ChatHistoryContainer ref={historyContainerRef}>
        <ChatHistory history={history} />
        {/* Mostrar indicador se isTyping for true */}
        {isTyping && (
          <TypingIndicator>
            Carla está digitando...
            <span></span><span></span><span></span>
          </TypingIndicator>
        )}
        {/* Loader original removido daqui, pois isTyping serve como indicador */}
        {/* {loading && <Loader />} */}
      </ChatHistoryContainer>
      <FooterContainer>
        <ChatInput onSend={sendMessage} loading={loading || isTyping} /> {/* Desabilitar input enquanto digita */}
      </FooterContainer>
    </ChatViewContainer>
  );
}
